// Blender-authored hover skiffs: brass hull, dark pontoons, an aether strip that
// brightens with speed, and a gentle bob. Synced from the world model each
// frame; the ridden skiff follows the local prediction, everyone else's
// follows the interpolated feed. Hull geometry and the common materials
// are shared across every skiff (entries come and go with interest
// management); only the strip material — whose emissive intensity is
// per-skiff — is owned per entry and disposed on removal.

import { SKIFF_HOVER } from "@bebop/shared";
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { World } from "../core/world";
import { contactShadow } from "./attire";
import { cloneAuthoredAsset, disposeAuthoredObject, getAuthoredTemplate } from "./authored-assets";
import { Tracker } from "./common";

interface SkiffEntry {
  root: THREE.Group;
  strip: THREE.MeshStandardMaterial;
  bobPhase: number;
}

/** Aether-strip colour by wear band: gold running true, souring toward a
 *  dead verdigris on a derelict. The machine is the gauge. */
const STRIP_LOOK: Record<string, number> = {
  true: 0xd8b04a,
  scuffed: 0xc9a24a,
  rough: 0xb08d40,
  sour: 0x9aa845,
  derelict: 0x5f6b58,
};

interface SharedParts {
  /**
   * EVERY brass member of the machine, merged: hull, prow, the four pylons,
   * the stern tie-beam and the eight pontoon end pieces, in one geometry.
   *
   * Fixing the floating pontoons meant seven more members a side, and a skiff
   * is not a rare object — the settlement stands six of them and interest
   * management builds one mesh per entry. Merged, the whole brass body is one
   * draw call, so the machine now costs THREE FEWER calls than it did with
   * the bug in it (nine meshes down to six).
   */
  body: THREE.BufferGeometry;
  pontoon: THREE.BufferGeometry;
  seat: THREE.BufferGeometry;
  strip: THREE.BufferGeometry;
  brass: THREE.Material;
  dark: THREE.Material;
  /** The pool a hovering machine puts on the ground under it. */
  shadowGeo: THREE.BufferGeometry;
  shadowMat: THREE.Material;
}

function createSkiffParts(t: Tracker): SharedParts {
  // One prototype, shared: contactShadow() mints a material per call, and a
  // skiff layer builds one mesh per entry as interest management churns.
  const proto = contactShadow();
  return {
    shadowGeo: proto.geometry,
    shadowMat: t.mat(proto.material as THREE.Material),
    body: t.geo(skiffBody()),
    pontoon: t.geo(new THREE.CapsuleGeometry(0.21, 1.68, 3, 10)),
    seat: t.geo(new THREE.BoxGeometry(0.7, 0.26, 0.9)),
    strip: t.geo(new THREE.BoxGeometry(1.34, 0.06, 1.8)),
    brass: t.mat(
      // METALNESS 1, not 0.65. Brass is a metal; two thirds of one is a
      // substance that keeps a third of a diffuse lobe and tints a third of
      // its specular, which is the uncanny middle the material-response pass
      // exists to delete. The colour is the reflection tint, which is what a
      // metal's "albedo" is.
      new THREE.MeshStandardMaterial({ color: 0x8a6a3a, metalness: 1, roughness: 0.38 }),
    ),
    // Lifted off #1c1a22 (wave 12). At night that value was a silhouette; in
    // the DAY street it was a hole — an unlit black disc against Y158 playa,
    // which is half of why the pontoons read as absences rather than parts.
    // A little metalness gives the capsule's crown a terminator to roll a
    // highlight along, which is the other half.
    dark: t.mat(
      new THREE.MeshStandardMaterial({ color: 0x272430, roughness: 0.68, metalness: 0.16 }),
    ),
  };
}

/**
 * A parked skiff as street dressing (wave 9): the same silhouette players
 * ride, engine cold (dim strip), brass polished enough to catch the sun
 * probe — the settlement's staged mid-ground metal subject (§e hero-metal,
 * t011's staged ship). Static décor: no sync, no collision (like planters'
 * cousins that never grew hitboxes — logged as a known fiction gap).
 */
export function buildParkedSkiff(t: Tracker): THREE.Group {
  const parts = createSkiffParts(t);
  const brass = parts.brass as THREE.MeshStandardMaterial;
  brass.roughness = 0.22;
  // Hero-metal probe read (§e): the r2 critics saw "matte dark lumps".
  // Against the round-3 scene environmentIntensity of 0.16, this boost
  // lets polished brass catch the probe's unclipped sun disc.
  brass.envMapIntensity = 6.5;
  const strip = t.mat(
    new THREE.MeshStandardMaterial({
      color: 0xd8b04a,
      emissive: 0xd8b04a,
      emissiveIntensity: 0.22,
    }),
  );
  // Parked décor sits at y = 0.42 (render/surface.ts), so that is how far
  // its shadow has to reach back down to the pad.
  const g = buildSkiffMesh(parts, strip as THREE.MeshStandardMaterial, 0.42);
  g.traverse((obj) => {
    if ((obj as THREE.Mesh).isMesh) obj.receiveShadow = true;
  });
  return g;
}

/** Pontoon geometry: centre offset and hover-line, shared by body and mesh. */
const PONTOON_X = 0.8;
const PONTOON_Y = -0.16;

/**
 * THE TWO FLOATING BLACK DISCS, WAVE FOUR — and this time with the
 * measurement instead of a guess.
 *
 * The artifact outlived four rounds of orders to delete it because nobody had
 * ever established what it was. It is not a detached hand, an accessory on a
 * missing bone, a prop instanced at the player's position, or an unmounted
 * hull mount point. A raycast through the two dark shapes in
 * `cw-fix-street.png` (px 1028,580 and 1117,579) lands on the PONTOONS of a
 * skiff at world (−18, 1.1, 3.2) — a live machine standing in the street
 * 5.6 m BEHIND the avatar, whose two floats happen to project either side of
 * the figure's shoulders from that camera. They were never on the avatar;
 * they only ever straddled it in screen space, which is why four waves of
 * searching the humanoid rig came back empty.
 *
 * And they really were floating, by 1 cm of arithmetic. Hull half-width 0.65;
 * pontoon centre 0.85 with radius 0.21, so its inboard face sat at 0.64 —
 * TANGENT to the hull, touching along a hairline and showing daylight through
 * the join from almost every angle. Wave 11 answered the complaint by
 * re-modelling the pontoon (cylinder → capsule, plus a brass nose cone)
 * without ever moving it, which is why the note came back word for word with
 * brass on it.
 *
 * The fix is structural, and it is four things:
 *   • The pontoon moves inboard and up to 0.80 / −0.16, so it genuinely
 *     INTERSECTS the hull instead of grazing it — 6 cm of overlap, which no
 *     amount of sub-pixel coverage can open a gap in.
 *   • Two PYLONS a side carry it, fore and aft. A gap you can see through is
 *     only half the problem; the other half is that a float with no visible
 *     means of support is not attached even when it is touching.
 *   • A stern TIE-BEAM spans pontoon to pontoon behind the hull. Dead astern
 *     — which is exactly the pose the street vista catches this machine in —
 *     that beam is the member that ties both floats and the hull into ONE
 *     silhouette, so no camera is left from which the ends read as two
 *     objects hanging in mid-air.
 *   • Both ends of each float are finished: the nose cone it already had, and
 *     a bell on the aft end, because the aft end is the one that faces a
 *     street and a bare capsule cap facing you is the dark disc all over
 *     again.
 *
 * All of it is merged into ONE brass geometry — see SharedParts.body.
 */
function skiffBody(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const push = (
    geo: THREE.BufferGeometry,
    x: number,
    y: number,
    z: number,
    rx = 0,
    ry = 0,
  ): void => {
    if (rx) geo.rotateX(rx);
    if (ry) geo.rotateY(ry);
    geo.translate(x, y, z);
    parts.push(geo);
  };

  push(new THREE.BoxGeometry(1.3, 0.34, 2.6), 0, 0, 0);
  // Prow: nose cone laid flat along +z (the yaw-forward axis).
  push(new THREE.ConeGeometry(0.62, 0.9, 4), 0, 0, 1.72, Math.PI / 2, Math.PI / 4);

  for (const side of [-1, 1]) {
    push(new THREE.ConeGeometry(0.2, 0.3, 10), side * PONTOON_X, PONTOON_Y, 1.03, Math.PI / 2);
    push(
      new THREE.ConeGeometry(0.24, 0.26, 10, 1, true),
      side * PONTOON_X,
      PONTOON_Y,
      -1.06,
      -Math.PI / 2,
    );
    for (const z of [-0.72, 0.72]) {
      push(new THREE.BoxGeometry(0.34, 0.3, 0.26), side * (PONTOON_X - 0.14), PONTOON_Y + 0.14, z);
    }
  }
  push(new THREE.BoxGeometry(1.94, 0.14, 0.16), 0, PONTOON_Y + 0.08, -1.02);

  const merged = mergeGeometries(parts);
  for (const geo of parts) geo.dispose();
  if (!merged) throw new Error("skiff body merge failed");
  return merged;
}

/** `hover` is the machine's height above the ground it is standing over. */
function buildSkiffMesh(
  parts: SharedParts,
  strip: THREE.MeshStandardMaterial,
  hover: number,
): THREE.Group {
  const g = new THREE.Group();

  if (getAuthoredTemplate("hover-skiff")) {
    const hull = cloneAuthoredAsset("hover-skiff");
    hull.position.y = -0.42;
    const replaced = new Set<THREE.Material>();
    hull.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const bindBurner = (material: THREE.Material): THREE.Material => {
        if (material.name !== "vehicle-aether") return material;
        replaced.add(material);
        return strip;
      };
      object.material = Array.isArray(object.material)
        ? object.material.map(bindBurner)
        : bindBurner(object.material);
    });
    for (const material of replaced) material.dispose();
    g.add(hull);
    // Keep the existing per-machine wear/charge gauge independent of the
    // shared authored materials; the strip sits inset along the painted prow.
    const gauge = new THREE.Mesh(parts.strip, strip);
    gauge.scale.set(0.052, 0.34, 0.4);
    gauge.position.set(0, 0.214, 1.2);
    gauge.rotation.x = 0.11;
    g.add(gauge);
  } else {
    g.add(new THREE.Mesh(parts.body, parts.brass));

    for (const side of [-1, 1]) {
      const pontoon = new THREE.Mesh(parts.pontoon, parts.dark);
      pontoon.rotation.x = Math.PI / 2;
      pontoon.position.set(side * PONTOON_X, PONTOON_Y, 0);
      g.add(pontoon);
    }

    const seat = new THREE.Mesh(parts.seat, parts.dark);
    seat.position.set(0, 0.3, -0.5);
    g.add(seat);

    const stripMesh = new THREE.Mesh(parts.strip, strip);
    stripMesh.position.set(0, 0.21, 0.3);
    g.add(stripMesh);
  }

  g.traverse((obj) => {
    if ((obj as THREE.Mesh).isMesh) obj.castShadow = true;
  });

  // A hovering machine with nothing under it is a FLOATING machine. The
  // pool is the whole difference, and the night sets — which run no
  // shadow-casting light at all — have never had one. Added after the mesh
  // so the castShadow sweep above cannot cast a shadow from a shadow.
  const pool = new THREE.Mesh(parts.shadowGeo, parts.shadowMat);
  pool.rotation.x = -Math.PI / 2;
  pool.renderOrder = -1;
  pool.scale.setScalar(3.1);
  // A shade proud of the ground: the bob is ±6 cm and a pool sunk under a
  // slope simply stops drawing.
  pool.position.y = -hover + 0.06;
  g.add(pool);
  return g;
}

export class SkiffLayer {
  /** Parent under the area's world anchor. */
  readonly group = new THREE.Group();
  private tracker = new Tracker();
  private parts: SharedParts;
  private entries = new Map<string, SkiffEntry>();
  private elapsed = 0;

  /** Ground elevation under a world position; the area scene supplies it. */
  groundY: (x: number, z: number) => number = () => 0;

  constructor() {
    this.parts = createSkiffParts(this.tracker);
  }

  sync(world: World, dt: number): void {
    this.elapsed += dt;

    for (const [id, entry] of this.entries) {
      if (!world.skiffs.has(id)) {
        this.group.remove(entry.root);
        disposeAuthoredObject(entry.root);
        entry.strip.dispose();
        this.entries.delete(id);
      }
    }

    for (const [id, skiff] of world.skiffs) {
      let entry = this.entries.get(id);
      if (!entry) {
        const strip = new THREE.MeshStandardMaterial({
          color: 0xd8b04a,
          emissive: 0xd8b04a,
          emissiveIntensity: 0.7,
        });
        entry = {
          root: buildSkiffMesh(this.parts, strip, SKIFF_HOVER),
          strip,
          bobPhase: (id.length * 2.39 + id.charCodeAt(id.length - 1)) % (Math.PI * 2),
        };
        this.entries.set(id, entry);
        this.group.add(entry.root);
      }

      const pose = world.selfSkiff?.id === id ? world.selfSkiff.pose : skiff.renderPose;
      const speed = Math.abs(world.selfSkiff?.id === id ? world.selfSkiff.pose.speed : skiff.speed);
      const bob = Math.sin(this.elapsed * 1.7 + entry.bobPhase) * 0.06 * (1 - speed / 25);
      entry.root.position.set(pose.x, this.groundY(pose.x, pose.z) + SKIFF_HOVER + bob, pose.z);
      entry.root.rotation.y = pose.yaw;
      // The wear band drives the strip's colour and the charge drives its
      // life: a sour skiff reads sour across a street before anyone opens
      // a panel, and a dry one barely glows. The strip material is owned
      // per entry — the shared brass is never touched (one lerp, no bar).
      const look = STRIP_LOOK[skiff.wearBand] ?? 0xd8b04a;
      entry.strip.color.setHex(look);
      entry.strip.emissive.setHex(look);
      const base = skiff.chargeBand === "dry" ? 0.12 : 0.7;
      entry.strip.emissiveIntensity = base + (speed / 16.5) * 1.6;
    }
  }

  dispose(): void {
    for (const entry of this.entries.values()) {
      this.group.remove(entry.root);
      disposeAuthoredObject(entry.root);
      entry.strip.dispose();
    }
    this.entries.clear();
    this.tracker.dispose();
  }
}
